import React, { useState } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const Footer = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="mt-16 py-6 bg-gray-100 border-t border-gray-200">
      <div className="container mx-auto px-4">
        {/* Facebook Disclaimer */}
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 leading-relaxed">
            This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, This site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. As stipulated by law, we can not and do not make any guarantees about your ability to get results or earn any money with my ideas, information, tools or strategies. I just want to help you by giving great content, direction and strategies that worked well for me and my students and that I believe can help you move forward. All of my terms, privacy policies and disclaimers for this program and website can be accessed via the links. I feel transparency is important and I hold ourselves (you & me) to a high standard of integrity. Thanks for stopping by. I hope this training and content brings you a lot of value & results.
          </p>
        </div>
      
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <p className="text-gray-600 text-sm">&copy; {currentYear} AI Kids Academy. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Privacy Policy
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Privacy Policy</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">AI Kids Academy Privacy Policy</h3>
                  <p className="text-sm text-gray-700 mb-4">Effective Date: May 7, 2025</p>
                  
                  <div className="space-y-4 text-sm">
                    <section>
                      <h4 className="font-medium mb-2">1. Introduction</h4>
                      <p>AI Kids Academy ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and inform you of your privacy rights.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">2. Data We Collect</h4>
                      <p>We may collect the following types of information:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Personal identification information (Name, email address, phone number)</li>
                        <li>Contact information for parents/guardians</li>
                        <li>Technical data including IP address, browser type and version</li>
                        <li>Usage data about how you use our website</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">3. How We Use Your Data</h4>
                      <p>We use your data for the following purposes:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>To provide and maintain our service</li>
                        <li>To notify you about changes to our services</li>
                        <li>To provide customer support</li>
                        <li>To gather analysis to improve our services</li>
                        <li>To send you educational content, updates, and marketing communications (which you can opt out of)</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">4. Data Sharing</h4>
                      <p>We do not sell your personal information. We may share your data with:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Service providers who perform services for us</li>
                        <li>Analytical services for business improvement</li>
                        <li>Legal authorities when required by law</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">5. Cookies and Tracking</h4>
                      <p>We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">6. Data Security</h4>
                      <p>We have implemented appropriate security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">7. Your Data Protection Rights</h4>
                      <p>You have the right to:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Access your personal data</li>
                        <li>Request correction of your personal data</li>
                        <li>Request deletion of your personal data</li>
                        <li>Object to processing of your personal data</li>
                        <li>Request restriction of processing your personal data</li>
                        <li>Request transfer of your personal data</li>
                        <li>Withdraw consent</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">8. Children's Privacy</h4>
                      <p>We do not knowingly collect data from children under 13 without parental consent. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">9. Changes to This Privacy Policy</h4>
                      <p>We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">10. Contact Us</h4>
                      <p>If you have any questions about this privacy policy, please contact us at:</p>
                      <p className="mt-1">Email: privacy@aikidsacademy.com</p>
                    </section>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Terms of Service
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Terms of Service</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">AI Kids Academy Terms of Service</h3>
                  <p className="text-sm text-gray-700 mb-4">Last Updated: May 7, 2025</p>
                  
                  <div className="space-y-4 text-sm">
                    <p>By accessing or using our services, you agree to be bound by these Terms of Service. Our service is intended for educational purposes only. We reserve the right to modify these terms at any time.</p>
                    <p>For a complete copy of our Terms of Service, please contact us at terms@aikidsacademy.com.</p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Disclaimer
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Disclaimer</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">AI Kids Academy Disclaimer</h3>
                  <p className="text-sm text-gray-700 mb-4">Last Updated: May 7, 2025</p>
                  
                  <div className="space-y-4 text-sm">
                    <section>
                      <h4 className="font-medium mb-2">1. Facebook Disclaimer</h4>
                      <p>This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, This site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">2. Results Disclaimer</h4>
                      <p>As stipulated by law, we can not and do not make any guarantees about your ability to get results or earn any money with our ideas, information, tools or strategies. We just want to help you by giving great content, direction and strategies that have worked well for us and our students and that we believe can help you move forward.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">3. Educational Content</h4>
                      <p>All webinars, presentations and training provided are educational in nature and are provided with the understanding that AI Kids Academy is not rendering any professional advice or services to the individual learner.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">4. No Guarantees</h4>
                      <p>We make no guarantees regarding the financial or educational outcomes of using our information. Your level of success in attaining similar results is dependent upon a number of factors including your skill, knowledge, ability, dedication, educational background, and many other varying factors.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">5. Testimonials</h4>
                      <p>Any testimonials and examples shown through our website, programs, content are not to be interpreted as a promise or guarantee of results. Each individual's success depends on their background, dedication, desire and motivation.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">Contact Information</h4>
                      <p>For questions or concerns regarding this disclaimer, please contact:</p>
                      <p className="mt-1">Email: support@aikidsacademy.com</p>
                    </section>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* New Cancellation and Refund Policy */}
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Cancellation & Refund
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Cancellation and Refund Policy</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">AI Kids Academy Cancellation and Refund Policy</h3>
                  <p className="text-sm text-gray-700 mb-4">Last Updated: {new Date().toLocaleDateString()}</p>
                  
                  <div className="space-y-4 text-sm">
                    <section>
                      <h4 className="font-medium mb-2">1. Webinar Registration Refund Policy</h4>
                      <p>Due to the exclusive nature of our paid webinars and limited seating, all registration fees (₹99) are non-refundable once payment is completed.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">2. Cancellation by AI Kids Academy</h4>
                      <p>In the unlikely event that we need to cancel a webinar, all participants will receive a full refund within 7-10 business days. We will also offer priority access to the next available session.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">3. Technical Issues</h4>
                      <p>If you experience technical difficulties that prevent you from attending the live webinar, please contact us immediately. We will review each case individually and may offer:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Access to recorded session (if available)</li>
                        <li>Transfer to next available webinar</li>
                        <li>Partial refund at our discretion</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">4. Emergency Situations</h4>
                      <p>We understand that emergencies happen. If you have a genuine emergency that prevents attendance, please contact us within 24 hours of the webinar with documentation. We will consider refund requests on a case-by-case basis.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">5. Processing Time</h4>
                      <p>Approved refunds will be processed within 7-10 business days and will be credited back to the original payment method used during registration.</p>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">6. Contact for Refund Requests</h4>
                      <p>All refund requests must be submitted via email to:</p>
                      <p className="mt-1 font-medium">Email: refunds@aikidsacademy.com</p>
                      <p className="mt-1">Include your payment ID and reason for refund request.</p>
                    </section>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* New Shipping and Delivery Policy */}
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Shipping & Delivery
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Shipping and Delivery Policy</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">AI Kids Academy Shipping and Delivery Policy</h3>
                  <p className="text-sm text-gray-700 mb-4">Last Updated: {new Date().toLocaleDateString()}</p>
                  
                  <div className="space-y-4 text-sm">
                    <section>
                      <h4 className="font-medium mb-2">1. Digital Service Delivery</h4>
                      <p>AI Kids Academy primarily offers digital educational services including webinars, online courses, and digital materials. All services are delivered electronically:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Webinar access links sent via email</li>
                        <li>Digital course materials available for instant download</li>
                        <li>No physical shipping required for standard services</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">2. Webinar Access Delivery</h4>
                      <p>Upon successful payment registration:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Confirmation email sent immediately after payment</li>
                        <li>Facebook group invitation within 24 hours</li>
                        <li>Webinar reminder sent 1 day before the event</li>
                        <li>Final access instructions sent 1 hour before webinar start time</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">3. Physical Materials (If Applicable)</h4>
                      <p>In case we offer physical educational materials or merchandise:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Shipping within India: 3-7 business days</li>
                        <li>International shipping: 7-15 business days</li>
                        <li>Shipping costs will be clearly displayed at checkout</li>
                        <li>Tracking information provided once items are dispatched</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">4. Delivery Issues</h4>
                      <p>If you don't receive your digital access or have delivery issues:</p>
                      <ul className="list-disc pl-5 mt-2">
                        <li>Check your spam/junk folder first</li>
                        <li>Ensure the email address provided is correct</li>
                        <li>Contact support immediately for assistance</li>
                        <li>We guarantee resolution within 24 hours</li>
                      </ul>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">5. Contact for Delivery Support</h4>
                      <p>For any delivery-related queries:</p>
                      <p className="mt-1">Email: delivery@aikidsacademy.com</p>
                      <p className="mt-1">Response time: Within 24 hours</p>
                    </section>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* New Contact Us */}
            <Sheet>
              <SheetTrigger asChild>
                <button className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
                  Contact Us
                </button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Contact Us</SheetTitle>
                </SheetHeader>
                <div className="py-4 text-left">
                  <h3 className="text-lg font-semibold mb-2">Get in Touch with AI Kids Academy</h3>
                  <p className="text-sm text-gray-700 mb-4">We're here to help! Reach out to us for any questions or support.</p>
                  
                  <div className="space-y-4 text-sm">
                    <section>
                      <h4 className="font-medium mb-2">📧 Email Support</h4>
                      <div className="space-y-2">
                        <p><strong>General Inquiries:</strong> info@aikidsacademy.com</p>
                        <p><strong>Technical Support:</strong> support@aikidsacademy.com</p>
                        <p><strong>Refunds & Billing:</strong> refunds@aikidsacademy.com</p>
                        <p><strong>Partnership Inquiries:</strong> partnerships@aikidsacademy.com</p>
                        <p className="text-gray-600">Response time: Within 24 hours</p>
                      </div>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">📱 Social Media</h4>
                      <div className="space-y-2">
                        <p><strong>Facebook Group:</strong> AI Kids Academy Community</p>
                        <p><strong>Instagram:</strong> @aikidsacademy</p>
                        <p><strong>LinkedIn:</strong> AI Kids Academy</p>
                        <p className="text-gray-600">Follow us for updates and educational content!</p>
                      </div>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">🕒 Support Hours</h4>
                      <div className="space-y-1">
                        <p><strong>Monday - Friday:</strong> 9:00 AM - 7:00 PM IST</p>
                        <p><strong>Saturday:</strong> 10:00 AM - 4:00 PM IST</p>
                        <p><strong>Sunday:</strong> Closed (Emergency support available)</p>
                      </div>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">❓ Frequently Asked Questions</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium">Q: How do I access the webinar?</p>
                          <p className="text-gray-700">A: After payment, you'll receive Facebook group access and webinar links via email.</p>
                        </div>
                        <div>
                          <p className="font-medium">Q: What if I miss the live webinar?</p>
                          <p className="text-gray-700">A: This is a live-only event. Recording may be available to registered participants only.</p>
                        </div>
                        <div>
                          <p className="font-medium">Q: Is this suitable for my child's age?</p>
                          <p className="text-gray-700">A: Our content is designed for parents of children aged 6-16 years.</p>
                        </div>
                      </div>
                    </section>
                    
                    <section>
                      <h4 className="font-medium mb-2">🏢 Business Information</h4>
                      <div className="space-y-1">
                        <p><strong>Company:</strong> AI Kids Academy</p>
                        <p><strong>Address:</strong> [Your Business Address]</p>
                        <p><strong>GST No:</strong> [Your GST Number]</p>
                        <p><strong>CIN:</strong> [Your Company Identification Number]</p>
                      </div>
                    </section>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
